import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IAccount, IContactFieldOptions, ICustomSurvey, IRichContentEditorState } from '../../../../../extViewmodels';
import { ToolbarCustomSurvey } from '../../../../../models/UiUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { THEME } from '../../../../components-v2/Theme/theme';
import { DangerIcon } from '../../../../components/svgs/icons/DangerIcon';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../../components/richContent/RichContentDocumentEditor';
import { Input } from '../../../../components-v2/Input';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { ShowSurveyNameToggle } from '../../shared/components/ShowSurveyNameToggle';
import { TagOnCompletion } from '../../shared/components/TagOnCompletion';
import { usePreviewMode } from '../../shared/contexts/PreviewModeContext';
import { isV2SurveyForm } from '../../shared/components/QuestionsWithSections/utils/transform';
import { useSurveyHeaderCustomizationStatus } from '../../shared/hooks';
import { styleSheet } from '../styles';
import { CustomSurveyFormData } from '../utils';

/**
 * Warning-styled lock note rendered in a field's left text column (via FormFieldWrapper's
 * `descriptionNote` slot). Pairs a warning icon with the message so the irreversible/required
 * constraint stands out. Used for both the partial-submission and email-required lock notes.
 */
const PartialLockNote = ({ message }: { message: string }) => (
	<span className={css(styleSheet.lockNoteRow)}>
		<DangerIcon
			className={css(styleSheet.lockNoteIcon)}
			backgroundColor={THEME.colors.warning.main}
			borderColor={THEME.colors.warning.main}
		/>
		<span className={css(styleSheet.partialSubmissionNote)}>{message}</span>
	</span>
);

const config: IRichContentDocumentEditorConfig = {
	plugins: 'autolink',
	toolbar: ToolbarCustomSurvey,
};

// Helper function to get current dropdown selection and display text based on form values
const getDropdownText = (collect: boolean, require: boolean): string => {
	if (require && collect) return 'Required';
	if (collect && !require) return 'Collect';
	return 'Do Not Collect';
};

// Helper function to handle dropdown selection changes
const handleDropdownChange = <T extends string>(
	value: string,
	fieldName: T,
	setValue: (name: T, value: IContactFieldOptions, options?: { shouldDirty?: boolean }) => void,
	currentUpdateExisting = false
) => {
	const contactOptions: IContactFieldOptions = {};

	switch (value) {
		case 'Required':
			contactOptions.collect = true;
			contactOptions.require = true;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
		case 'Collect':
			contactOptions.collect = true;
			contactOptions.require = false;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
		case 'Do Not Collect':
		default:
			contactOptions.collect = false;
			contactOptions.require = false;
			contactOptions.updateExisting = currentUpdateExisting;
			break;
	}

	setValue(fieldName, contactOptions, { shouldDirty: true });
};

export const BasicInfoSettings = ({ survey: _survey, account }: { survey: ICustomSurvey; account: IAccount }) => {
	const { control, watch, setValue } = useFormContext<CustomSurveyFormData>();
	const { isPreviewOnly } = usePreviewMode();
	const formValues = watch();

	/**
	 * One-way lock: once a survey has been SAVED with allowPartialSubmission=true
	 * the backend no longer accepts disabling it. Lock the toggle ON when the loaded
	 * survey already has it enabled.
	 */
	const lockedOn = !!_survey?.allowPartialSubmission;

	/**
	 * When partial submission is ON for a V2 survey, email must be collect+required.
	 * Lock the email controls to prevent the user from undoing that. Include `lockedOn`
	 * so a saved partial survey locks the email control immediately on load, before the
	 * form value hydrates — otherwise the dropdown is briefly editable on first render.
	 */
	const emailLockedByPartial =
		isV2SurveyForm(formValues.surveyForm) && (!!formValues.allowPartialSubmission || lockedOn);

	const { data: customHeaderData, hasSurveyHeaderCustomization } = useSurveyHeaderCustomizationStatus();

	// Without a header customization for surveys the survey name is always rendered by the default
	// header, so the toggle is locked on (disabled, with an explanatory info tooltip) and the value is
	// forced on. The toggle only has an effect when a custom header replaces that default header. We
	// gate on `!error` so a failed status load — which also reports `hasCustomization: false` — does
	// not silently overwrite a value the user saved.
	React.useEffect(() => {
		if (customHeaderData && !customHeaderData.error && !hasSurveyHeaderCustomization) {
			setValue('options.showSurveyName', true);
		}
	}, [customHeaderData, hasSurveyHeaderCustomization, setValue]);

	return (
		<section className={css(styleSheet.tabsControllerWrap)}>
			<h3>Basic Information</h3>
			<FormFieldWrapper
				name='updateExistingContacts'
				control={control}
				title='Update Existing Contact Information'
				description='Overwrite existing contact data with new information from survey responses'
			>
				{({ value, onChange }) => (
					<Toggle
						id='update-existing-contacts-checkbox'
						isOn={!!value}
						disabled={isPreviewOnly}
						onToggleCheckChanged={() => {
							const newValue = !value;

							// Update the toggle state
							onChange(newValue);

							if (newValue) {
								// When turning ON: If any field is "Do Not Collect", change it to "Collect"
								// If it's already "Required", leave it as "Required"
								const currentOptions = formValues.options;

								// Helper to ensure at least collect is true
								const ensureCollecting = (fieldOptions?: Api.IContactFieldOptions): Api.IContactFieldOptions => {
									if (!fieldOptions?.collect) {
										// If not collecting, set to collect (but not required)
										return { collect: true, require: false, updateExisting: true };
									}
									// Already collecting (either collect or required), just update the updateExisting flag
									return { ...fieldOptions, updateExisting: true };
								};

								setValue('options.contactNameOptions', ensureCollecting(currentOptions?.contactNameOptions), {
									shouldDirty: true,
								});
								setValue('options.contactEmailOptions', ensureCollecting(currentOptions?.contactEmailOptions), {
									shouldDirty: true,
								});
								setValue(
									'options.contactPhoneNumberOptions',
									ensureCollecting(currentOptions?.contactPhoneNumberOptions),
									{ shouldDirty: true }
								);
								setValue('options.contactBirthdayOptions', ensureCollecting(currentOptions?.contactBirthdayOptions), {
									shouldDirty: true,
								});
								setValue('options.contactAddressOptions', ensureCollecting(currentOptions?.contactAddressOptions), {
									shouldDirty: true,
								});
							} else {
								// When turning OFF: Only update the updateExisting flag, leave dropdown selections as-is
								setValue(
									'options.contactNameOptions',
									{
										...formValues.options?.contactNameOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactEmailOptions',
									{
										...formValues.options?.contactEmailOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactPhoneNumberOptions',
									{
										...formValues.options?.contactPhoneNumberOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactBirthdayOptions',
									{
										...formValues.options?.contactBirthdayOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
								setValue(
									'options.contactAddressOptions',
									{
										...formValues.options?.contactAddressOptions,
										updateExisting: false,
									},
									{ shouldDirty: true }
								);
							}
						}}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			{isV2SurveyForm(formValues.surveyForm) && (
				<FormFieldWrapper
					name='allowPartialSubmission'
					control={control}
					title='Allow Partial Submission'
					description='Let respondents save an incomplete response and return to finish later'
					// Render the lock note in the left text column (beneath the description) rather than
					// beside the toggle: the control slot is intentionally narrow, so a note there only
					// fits on very wide viewports and otherwise wraps into and displaces the toggle.
					descriptionNote={lockedOn ? <PartialLockNote message="Can't be disabled once enabled" /> : undefined}
				>
					{({ value, onChange }) => (
						<Toggle
							id='allow-partial-submission-toggle'
							isOn={lockedOn || !!value}
							disabled={lockedOn || isPreviewOnly}
							onToggleCheckChanged={() => {
								const turningOn = !value;
								onChange(turningOn);

								if (turningOn) {
									// BE requires email to be collect+required when partial submission is on
									const currentEmail = formValues.options?.contactEmailOptions;
									setValue(
										'options.contactEmailOptions',
										{ ...currentEmail, collect: true, require: true },
										{ shouldDirty: true }
									);
								}
							}}
							uncheckedColor={grayIconFill}
						/>
					)}
				</FormFieldWrapper>
			)}
			<FormFieldWrapper
				name='options.contactNameOptions'
				control={control}
				title='Contact Name'
				description='Collect first and last name from respondents'
				textWrapperStyles={[styleSheet.contactCollectionTextWrapper]}
			>
				{() => {
					const nameOptions = formValues.options?.contactNameOptions;
					const currentSelection = getDropdownText(nameOptions?.collect ?? false, nameOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactNameOptions',
									setValue,
									nameOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-name-dropdown' styles={[styleSheet.contactCollectionSelectTrigger]}>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactEmailOptions'
				control={control}
				title='Contact Email'
				description='Collect email address from respondents'
				textWrapperStyles={[styleSheet.contactCollectionTextWrapper]}
				// Same rationale as the partial-submission lock note: keep this lock note in the left
				// text column instead of the narrow control slot, where it would wrap into the dropdown.
				descriptionNote={
					emailLockedByPartial ? <PartialLockNote message='Required for partial submissions' /> : undefined
				}
			>
				{() => {
					const emailOptions = formValues.options?.contactEmailOptions;
					const currentSelection = getDropdownText(emailOptions?.collect ?? false, emailOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={emailLockedByPartial || isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactEmailOptions',
									setValue,
									emailOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-email-dropdown' styles={[styleSheet.contactCollectionSelectTrigger]}>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactPhoneNumberOptions'
				control={control}
				title='Contact Phone Number'
				description='Collect phone number from respondents'
				textWrapperStyles={[styleSheet.contactCollectionTextWrapper]}
			>
				{() => {
					const phoneOptions = formValues.options?.contactPhoneNumberOptions;
					const currentSelection = getDropdownText(phoneOptions?.collect ?? false, phoneOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactPhoneNumberOptions',
									setValue,
									phoneOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-phone-dropdown' styles={[styleSheet.contactCollectionSelectTrigger]}>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactBirthdayOptions'
				control={control}
				title='Contact Birthday'
				description='Collect birthday from respondents'
				textWrapperStyles={[styleSheet.contactCollectionTextWrapper]}
			>
				{() => {
					const birthdayOptions = formValues.options?.contactBirthdayOptions;
					const currentSelection = getDropdownText(
						birthdayOptions?.collect ?? false,
						birthdayOptions?.require ?? false
					);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactBirthdayOptions',
									setValue,
									birthdayOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-birthday-dropdown' styles={[styleSheet.contactCollectionSelectTrigger]}>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.contactAddressOptions'
				control={control}
				title='Contact Address'
				description='Collect mailing address from respondents'
				textWrapperStyles={[styleSheet.contactCollectionTextWrapper]}
			>
				{() => {
					const addressOptions = formValues.options?.contactAddressOptions;
					const currentSelection = getDropdownText(addressOptions?.collect ?? false, addressOptions?.require ?? false);

					return (
						<Select
							value={currentSelection}
							disabled={isPreviewOnly}
							onValueChange={(value: string) => {
								handleDropdownChange(
									value,
									'options.contactAddressOptions',
									setValue,
									addressOptions?.updateExisting ?? false
								);
							}}
						>
							<SelectTrigger id='contact-address-dropdown' styles={[styleSheet.contactCollectionSelectTrigger]}>
								<SelectValue placeholder='Select an option'>{currentSelection}</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='Do Not Collect'>Do Not Collect</SelectItem>
								<SelectItem value='Collect'>Collect</SelectItem>
								<SelectItem value='Required'>Required</SelectItem>
							</SelectContent>
						</Select>
					);
				}}
			</FormFieldWrapper>
			<FormFieldWrapper
				name='options.sendConfirmationMessage'
				control={control}
				title='Send Confirmation Message'
				description='Send an email confirmation to respondents after survey submission'
			>
				{({ value, onChange }) => (
					<Toggle
						id='send-confirmation-message-checkbox'
						isOn={value}
						disabled={isPreviewOnly}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			{/* The company logo only renders in the default header. A custom header replaces that header
			    entirely, so the toggle would do nothing and is hidden — the inverse of the survey-name
			    toggle below, which only has an effect when a custom header exists. */}
			{account?.preferences?.logo?.url && !hasSurveyHeaderCustomization && (
				<FormFieldWrapper
					name='options.includeCompanyLogo'
					control={control}
					title='Include Company Logo'
					description='Display your company logo at the top of the survey'
				>
					{({ value, onChange }) => (
						<Toggle
							id='include-company-logo-checkbox'
							isOn={value}
							disabled={isPreviewOnly}
							onToggleCheckChanged={() => onChange(!value)}
							uncheckedColor={grayIconFill}
						/>
					)}
				</FormFieldWrapper>
			)}
			<ShowSurveyNameToggle<CustomSurveyFormData>
				name='options.showSurveyName'
				control={control}
				description='Display the survey name at the top of the survey'
				id='show-survey-name-checkbox'
				disabled={isPreviewOnly}
				lockedReason={
					hasSurveyHeaderCustomization
						? undefined
						: 'The survey name is always shown at the top of the survey. This setting only takes effect when a custom survey header is applied to your account.'
				}
			/>
			<TagOnCompletion />
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='intro'>Intro message</label>
				<Controller
					name='options.intro'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<RichContentDocumentEditor
							className={css(styleSheet.editor)}
							config={config}
							contentState={formValues?.options?.intro as IRichContentEditorState}
							onContentStateChanged={fieldProps.onChange}
							readOnly={isPreviewOnly}
							readOnlyUseFullEditor={true}
							{...fieldProps}
						/>
					)}
				/>
			</div>
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='companyName'>Company Name</label>
				<Controller
					name='options.companyName'
					control={control}
					render={({ field: { ref, ...fieldProps } }) => (
						<Input id='companyName' type='text' disabled={isPreviewOnly} {...fieldProps} />
					)}
				/>
			</div>
		</section>
	);
};
